import ExpandMore from '@mui/icons-material/ExpandMore';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Collapse from '@mui/material/Collapse';
import Container from '@mui/material/Container';
import IconButton from '@mui/material/IconButton';
import { alpha } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { skipToken } from '@reduxjs/toolkit/query';
import ErrorImg from 'assets/images/404.webp';
import ChatPanel from 'components/common/ChatPanel';
import CodeMirrorEditor from 'components/editors/CodeMirrorEditor';
import SegmentedTabs from 'components/UI/SegmentedTabs';
import { type FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  type BusinessUnitPromptResult,
  type PlaygroundFunction,
  useGenerateRequestMutation,
} from 'services/api/playground.api';
import type { Message } from 'services/types/message';
import { NAVY, RADIUS_CARD, STATUS_WARN, SURFACE_CONTAINER_LOWEST } from 'styles/tokens';
import { DEFAULT_AI_MODEL } from 'utils/aiModels';

import { useGetMessageQuery } from '../../services/api/messages';
import AnalysisPanel from './components/AnalysisPanel';
import BusinessUnitGate from './components/BusinessUnitGate';
import CompareBoard from './components/CompareBoard';
import ConfigBar from './components/ConfigBar';
import ConversationThread from './components/ConversationThread';
import CreateEvalPanel from './components/CreateEvalPanel';
import OutputCard from './components/OutputCard';
import RunEvalsPanel from './components/RunEvalsPanel';
import ToolsEditor from './components/ToolsEditor';
import { usePlaygroundState } from './hooks/usePlaygroundState';
import type { Lane, PlaygroundTurn, TurnRole } from './types';
import {
  chatResultToLaneResult,
  conversationToTurns,
  laneResultToTurn,
  newUid,
  sanitizeConversation,
  toolCallsFromSerializedContent,
  turnsToConversation,
} from './utils/turnMapping';

// FUTURE(playground): save/load named scenarios — a scenario is just serialize(state); persist via a
// new backend table + GET/POST endpoints. State is intentionally local/ephemeral to keep this trivial.
// FUTURE(playground): client-persona simulation (angry/insistent/confused/nice) — prepend a persona
// instruction to the next user turn. Add a persona selector to ConfigBar when built.

const ErrorPage: FC = () => (
  <Container sx={{ mt: 4 }}>
    <Typography variant="h4" sx={{ mb: 2, color: NAVY, fontFamily: 'Jost, sans-serif' }}>
      Oops... El mensaje que buscas no existe. Revisa la url o elige otro mensaje.
    </Typography>
    <img src={ErrorImg} alt="Error" style={{ width: 'min(600px, 98%)' }} />
  </Container>
);

/** Normalize stored functions (bare OpenAIFunction or {type,function}) into the wire shape. */
const normalizeFunctions = (functions: Message['functions']): PlaygroundFunction[] => {
  if (!Array.isArray(functions)) {
    return [];
  }
  return functions.map((f) => {
    const entry = f as unknown as Record<string, unknown>;
    if (entry && entry.type === 'function' && entry.function) {
      return entry as unknown as PlaygroundFunction;
    }
    return { type: 'function', function: f };
  });
};

/**
 * Build the ground-truth lane from a loaded message: the real final response (for single mode) plus the
 * full original conversation = input context + that response (for compare-mode full-replay display).
 */
const groundTruthFromMessage = (message: Message): Lane | undefined => {
  if (!message.text && !message.functionName) {
    return undefined;
  }
  // The stored text can itself be a serialized tool-call array; without hydrating it the real reply reads
  // as a JSON blob and an eval built from it grades that blob as prose.
  const serializedToolCalls = message.functionName ? undefined : toolCallsFromSerializedContent(message.text);
  const finalTurn: PlaygroundTurn = {
    uid: newUid(),
    role: 'assistant',
    content: serializedToolCalls ? '' : (message.text ?? ''),
    toolCalls: message.functionName
      ? [{ id: 'ground-truth-call', name: message.functionName, arguments: message.functionArguments ?? '' }]
      : serializedToolCalls,
  };
  return {
    id: 'ground-truth',
    model: message.AIModel ?? 'desconocido',
    status: 'done',
    isGroundTruth: true,
    result: { content: finalTurn.content, toolCalls: finalTurn.toolCalls },
    turns: [...conversationToTurns(message.conversation), finalTurn],
  };
};

/** Parse a "$X.XX" cost string to a number for aggregating replay cost. */
const parseCostNum = (cost?: string): number => {
  if (!cost) {
    return 0;
  }
  const n = Number.parseFloat(cost.replace(/[^0-9.]/g, ''));
  return Number.isNaN(n) ? 0 : n;
};

const errorMessage = (e: unknown): string => {
  if (e && typeof e === 'object') {
    const err = e as { status?: number | string; data?: unknown; error?: string };
    // Structured JSON error body.
    const dataMsg = (err.data as { message?: string } | undefined)?.message;
    if (dataMsg) {
      return dataMsg;
    }
    // Plain-text error body (e.g. a 400 returning "Bad Request") — avoids a JSON-parse crash.
    if (typeof err.data === 'string' && err.data.trim()) {
      return err.data;
    }
    if (typeof err.status === 'number') {
      return `Error ${err.status} del servidor.`;
    }
    if (err.error) {
      return err.error;
    }
  }
  return 'Error al ejecutar el modelo.';
};

const sectionLabel = {
  fontFamily: 'Jost, sans-serif',
  fontWeight: 700,
  fontSize: 13,
  color: NAVY,
};

const Playground: FC = () => {
  const { messageId } = useParams<{ messageId: string }>();
  const [state, dispatch] = usePlaygroundState();
  const [generateRequest] = useGenerateRequestMutation();
  const [analysisOpen, setAnalysisOpen] = useState(false);
  const [regeneratingUid, setRegeneratingUid] = useState<string | null>(null);
  const [chatSending, setChatSending] = useState(false);
  const [promptOpen, setPromptOpen] = useState(true);
  const [evalPanelOpen, setEvalPanelOpen] = useState(false);
  const [runEvalsOpen, setRunEvalsOpen] = useState(false);

  // Hooks run unconditionally; skipToken avoids fetching in blank-start mode (fixes the old
  // conditional-hooks bug where useGetMessageQuery sat after an early return).
  const {
    data: message,
    isLoading: messageLoading,
    isError: messageError,
  } = useGetMessageQuery(messageId ? Number(messageId) : skipToken);

  // Seed state from a loaded message (ground-truth + input conversation).
  useEffect(() => {
    if (!message) {
      return;
    }
    dispatch({
      type: 'LOAD_FROM_MESSAGE',
      payload: {
        prompt: message.prompt ?? '',
        functions: normalizeFunctions(message.functions),
        turns: conversationToTurns(message.conversation),
        model: message.AIModel ?? DEFAULT_AI_MODEL,
        groundTruth: groundTruthFromMessage(message),
      },
    });
  }, [message, dispatch]);

  // Warn before leaving with unsaved in-memory edits; cleaned up so we don't leak the handler.
  useEffect(() => {
    if (!state.dirty) {
      return undefined;
    }
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [state.dirty]);

  const functionNames = useMemo(
    () => state.functions.map((f) => f.function?.name).filter((n): n is string => Boolean(n)),
    [state.functions]
  );
  const canRun = !state.conversationError && !state.functionsError;
  /**
   * The response an eval grades against. A thread loaded from a message holds only the input context —
   * the reply lives in the ground-truth lane — so fall back to whatever response is on screen: the model
   * output just generated, or the real one this session was loaded from.
   */
  const pendingResponse = state.primaryRun.result ?? state.groundTruth?.result;
  const turnsForEval = useMemo(() => {
    const lastTurn = state.turns[state.turns.length - 1];
    if (lastTurn?.role === 'assistant' || !pendingResponse) {
      return state.turns;
    }
    return [...state.turns, laneResultToTurn(pendingResponse)];
  }, [state.turns, pendingResponse]);

  const canCreateEval = !state.conversationError && turnsForEval.some((turn) => turn.role === 'assistant');

  const buildRequest = useCallback(
    (model: string) => ({
      conversation: sanitizeConversation(turnsToConversation(state.turns)),
      functions: state.functions,
      prompt: state.prompt,
      model,
      toolChoice: state.toolChoice,
    }),
    [state.turns, state.functions, state.prompt, state.toolChoice]
  );

  const onLoadBuPrompt = useCallback(
    (businessUnitId: number, result: BusinessUnitPromptResult) => {
      dispatch({
        type: 'LOAD_BU_PROMPT',
        payload: {
          businessUnitId,
          prompt: result.prompt,
          functions: (result.tools as PlaygroundFunction[]) ?? [],
          model: result.model ?? DEFAULT_AI_MODEL,
          promptIsFallback: result.promptIsFallback,
        },
      });
    },
    [dispatch]
  );

  const runSingle = useCallback(() => {
    dispatch({ type: 'PRIMARY_RUN_START' });
    generateRequest(buildRequest(state.model))
      .unwrap()
      .then((res) => dispatch({ type: 'PRIMARY_RUN_SUCCESS', result: chatResultToLaneResult(res) }))
      .catch((e) => dispatch({ type: 'PRIMARY_RUN_ERROR', error: errorMessage(e) }));
  }, [dispatch, generateRequest, buildRequest, state.model]);

  // Full replay: regenerate every assistant turn from scratch with the evaluated model. User/tool/system
  // turns are copied verbatim (the fixed client + environment side); each assistant turn is produced by
  // calling the model on the replay-so-far. Calls within a lane are sequential (each depends on the prior
  // turn); lanes run independently of each other. Tool results are reused from the original conversation.
  const replayLane = useCallback(
    async (lane: Lane) => {
      const original = state.turns;
      const assistantCount = original.filter((t) => t.role === 'assistant').length;
      // The real final reply lives in message.text (ground truth), not in `conversation`, so a loaded
      // conversation usually ends on a user/tool turn. Generate one extra assistant reply in that case so
      // the replay ends with the model's answer to the last turn (matching how the real case ends).
      const lastRole = original.length > 0 ? original[original.length - 1].role : null;
      const needsFinalAssistant = lastRole !== null && lastRole !== 'assistant';
      const total = assistantCount + (needsFinalAssistant ? 1 : 0);

      dispatch({
        type: 'PATCH_LANE',
        id: lane.id,
        patch: {
          status: 'running',
          turns: [],
          progress: { done: 0, total },
          cost: undefined,
          time: undefined,
          error: undefined,
        },
      });

      const replay: PlaygroundTurn[] = [];
      let done = 0;
      let costNum = 0;
      let timeSum = 0;

      const generateAssistantStep = async () => {
        const res = await generateRequest({
          conversation: sanitizeConversation(turnsToConversation(replay)),
          functions: state.functions,
          prompt: state.prompt,
          model: lane.model,
          toolChoice: state.toolChoice,
        }).unwrap();
        const result = chatResultToLaneResult(res);
        replay.push(laneResultToTurn(result));
        done += 1;
        costNum += parseCostNum(result.cost);
        timeSum += result.time ?? 0;
        dispatch({
          type: 'PATCH_LANE',
          id: lane.id,
          patch: { turns: [...replay], progress: { done, total }, cost: `$${costNum.toFixed(2)}`, time: timeSum },
        });
      };

      try {
        for (const turn of original) {
          if (turn.role === 'assistant') {
            // eslint-disable-next-line no-await-in-loop -- sequential by design: each turn depends on the prior
            await generateAssistantStep();
          } else {
            replay.push({ ...turn, uid: newUid() });
            dispatch({ type: 'PATCH_LANE', id: lane.id, patch: { turns: [...replay] } });
          }
        }
        if (needsFinalAssistant) {
          await generateAssistantStep();
        }
        dispatch({ type: 'PATCH_LANE', id: lane.id, patch: { status: 'done' } });
      } catch (e) {
        dispatch({ type: 'PATCH_LANE', id: lane.id, patch: { status: 'error', error: errorMessage(e) } });
      }
    },
    [state.turns, state.functions, state.prompt, state.toolChoice, generateRequest, dispatch]
  );

  // Each lane replays independently — one slow/failed model never blocks the others. The ground-truth
  // lane is filtered out: it is never re-run.
  const runAllLanes = useCallback(() => {
    state.compareLanes.forEach((lane) => {
      if (!lane.isGroundTruth) {
        void replayLane(lane);
      }
    });
  }, [state.compareLanes, replayLane]);

  // Regenerate an assistant turn: re-run the model on the conversation up to (excluding) it.
  const regenerateTurn = useCallback(
    (uid: string) => {
      const index = state.turns.findIndex((t) => t.uid === uid);
      if (index < 0) {
        return;
      }
      const priorConversation = sanitizeConversation(turnsToConversation(state.turns.slice(0, index)));
      setRegeneratingUid(uid);
      generateRequest({
        conversation: priorConversation,
        functions: state.functions,
        prompt: state.prompt,
        model: state.model,
        toolChoice: state.toolChoice,
      })
        .unwrap()
        .then((res) => {
          const result = chatResultToLaneResult(res);
          dispatch({ type: 'UPDATE_TURN', uid, patch: { content: result.content, toolCalls: result.toolCalls } });
        })
        .catch(() => {})
        .finally(() => setRegeneratingUid(null));
    },
    [state.turns, state.functions, state.prompt, state.model, state.toolChoice, generateRequest, dispatch]
  );

  // Live chat loop: append the user message, run the primary model on the updated conversation, then
  // append the assistant reply. The conversation is built from [...turns, userTurn] because the
  // reducer dispatch hasn't flushed into state.turns yet when we fire the request.
  const sendChat = useCallback(
    (text: string) => {
      const userTurn = { uid: newUid(), role: 'user' as TurnRole, content: text };
      dispatch({ type: 'APPEND_TURN', turn: userTurn });
      setChatSending(true);
      generateRequest({
        conversation: sanitizeConversation(turnsToConversation([...state.turns, userTurn])),
        functions: state.functions,
        prompt: state.prompt,
        model: state.model,
        toolChoice: state.toolChoice,
      })
        .unwrap()
        .then((res) => dispatch({ type: 'APPEND_TURN', turn: laneResultToTurn(chatResultToLaneResult(res)) }))
        .catch((e) =>
          dispatch({
            type: 'APPEND_TURN',
            turn: { uid: newUid(), role: 'assistant', content: `⚠️ ${errorMessage(e)}` },
          })
        )
        .finally(() => setChatSending(false));
    },
    [state.turns, state.functions, state.prompt, state.model, state.toolChoice, generateRequest, dispatch]
  );

  // ─── Render gates (after all hooks) ───────────────────────────────────────
  if (!messageId && !state.ready) {
    return (
      <ChatPanel>
        <ChatPanel.Header title="Playground" subtitle="Sandbox de simulación" />
        <ChatPanel.Body>
          <BusinessUnitGate onLoad={onLoadBuPrompt} />
        </ChatPanel.Body>
      </ChatPanel>
    );
  }
  if (messageId && messageLoading) {
    return (
      <Box sx={{ height: '100dvh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress sx={{ color: NAVY }} />
      </Box>
    );
  }
  if (messageId && (messageError || !message)) {
    return <ErrorPage />;
  }

  const subtitle = state.source === 'message' ? `Mensaje #${messageId}` : 'Blank-start';
  const primaryResult = state.primaryRun.result;

  return (
    <ChatPanel>
      <ChatPanel.Header
        title="Playground"
        subtitle={subtitle}
        rightAction={
          <SegmentedTabs
            tabs={['Single', 'Comparar']}
            value={state.mode === 'single' ? 0 : 1}
            onChange={(v) => dispatch({ type: 'SET_MODE', mode: v === 0 ? 'single' : 'compare' })}
          />
        }
      />
      <ChatPanel.Body>
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, p: 1.5, gap: 1.5 }}>
          <ConfigBar
            mode={state.mode}
            model={state.model}
            onModelChange={(model) => dispatch({ type: 'SET_MODEL', model })}
            toolChoice={state.toolChoice}
            onToolChoiceChange={(toolChoice) => dispatch({ type: 'SET_TOOL_CHOICE', toolChoice })}
            functionNames={functionNames}
            onRunSingle={runSingle}
            runningSingle={state.primaryRun.status === 'running'}
            canRun={canRun}
            onOpenAnalysis={() => setAnalysisOpen(true)}
            onCreateEval={() => setEvalPanelOpen(true)}
            canCreateEval={canCreateEval}
            onRunEvals={() => setRunEvalsOpen(true)}
          />

          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              gap: 1.5,
            }}
          >
            {/* Input column */}
            <Box
              sx={{
                flex: state.mode === 'compare' ? '0 0 340px' : 1,
                minWidth: 0,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: 1.5,
                bgcolor: SURFACE_CONTAINER_LOWEST,
                borderRadius: RADIUS_CARD,
                p: 1.5,
                overflowY: 'auto',
              }}
            >
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5, px: 0.5 }}>
                  <Box
                    sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', minWidth: 0 }}
                    onClick={() => setPromptOpen((v) => !v)}
                  >
                    <IconButton size="small" sx={{ p: 0.25, color: alpha(NAVY, 0.6) }}>
                      <ExpandMore
                        sx={{
                          fontSize: 18,
                          transform: promptOpen ? 'none' : 'rotate(-90deg)',
                          transition: 'transform 0.15s',
                        }}
                      />
                    </IconButton>
                    <Typography sx={sectionLabel}>Prompt del sistema</Typography>
                    {!promptOpen && state.prompt && (
                      <Typography sx={{ fontSize: 11, color: alpha(NAVY, 0.45), fontFamily: 'Jost, sans-serif' }}>
                        ({state.prompt.length} chars)
                      </Typography>
                    )}
                  </Box>
                  {state.promptIsFallback && (
                    <Box
                      sx={{
                        px: 1,
                        py: 0.25,
                        borderRadius: 999,
                        bgcolor: alpha(STATUS_WARN, 0.12),
                        color: STATUS_WARN,
                        fontSize: 10.5,
                        fontWeight: 700,
                        fontFamily: 'Jost, sans-serif',
                      }}
                    >
                      Sin interpolar (plantilla cruda)
                    </Box>
                  )}
                </Box>
                <Collapse in={promptOpen} unmountOnExit>
                  <CodeMirrorEditor
                    value={state.prompt}
                    onChange={(prompt) => dispatch({ type: 'SET_PROMPT', prompt })}
                    minHeight={180}
                    maxHeight={420}
                    ariaLabel="Prompt del sistema"
                  />
                </Collapse>
              </Box>

              <ToolsEditor
                functions={state.functions}
                onSetFunctions={(functions) => dispatch({ type: 'SET_FUNCTIONS', functions })}
                onError={(hasError) => dispatch({ type: 'SET_FUNCTIONS_ERROR', error: hasError })}
              />

              <Box sx={{ flex: 1, minHeight: 220 }}>
                <ConversationThread
                  turns={state.turns}
                  rawOpen={state.rawConversationOpen}
                  regeneratingUid={regeneratingUid}
                  showComposer={state.mode === 'single'}
                  chatSending={chatSending}
                  sendDisabled={state.functionsError}
                  onSendChat={sendChat}
                  onUpdateTurn={(uid, patch) => dispatch({ type: 'UPDATE_TURN', uid, patch })}
                  onDeleteTurn={(uid) => dispatch({ type: 'DELETE_TURN', uid })}
                  onAddTurn={(role: TurnRole) => dispatch({ type: 'ADD_TURN', role })}
                  onRegenerate={regenerateTurn}
                  onToggleRaw={(open) => dispatch({ type: 'TOGGLE_RAW_CONVERSATION', open })}
                  onSetTurns={(turns) => dispatch({ type: 'SET_TURNS', turns })}
                  onConversationError={(hasError) => dispatch({ type: 'SET_CONVERSATION_ERROR', error: hasError })}
                />
              </Box>
            </Box>

            {/* Output column */}
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                bgcolor: SURFACE_CONTAINER_LOWEST,
                borderRadius: RADIUS_CARD,
                p: 1.5,
                overflowY: state.mode === 'compare' ? 'hidden' : 'auto',
              }}
            >
              {state.mode === 'single' ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <Typography sx={{ ...sectionLabel, px: 0.5 }}>Respuesta</Typography>
                  {state.groundTruth && (
                    <OutputCard
                      label="Original"
                      model={state.groundTruth.model}
                      status="done"
                      result={state.groundTruth.result}
                      isGroundTruth
                    />
                  )}
                  <OutputCard
                    label={state.groundTruth ? 'Nueva respuesta' : 'Respuesta'}
                    model={state.model}
                    status={state.primaryRun.status}
                    result={state.primaryRun.result}
                    error={state.primaryRun.error}
                    onAppendToThread={
                      primaryResult
                        ? () => dispatch({ type: 'APPEND_TURN', turn: laneResultToTurn(primaryResult) })
                        : undefined
                    }
                  />
                </Box>
              ) : (
                <CompareBoard
                  groundTruth={state.groundTruth}
                  lanes={state.compareLanes}
                  anyRunning={state.compareLanes.some((l) => l.status === 'running')}
                  onAddLane={() => dispatch({ type: 'ADD_LANE', model: state.model })}
                  onRunAll={runAllLanes}
                  onChangeLaneModel={(id, model) => dispatch({ type: 'SET_LANE_MODEL', id, model })}
                  onRemoveLane={(id) => dispatch({ type: 'REMOVE_LANE', id })}
                  onRerunLane={(id) => {
                    const lane = state.compareLanes.find((l) => l.id === id);
                    if (lane) {
                      void replayLane(lane);
                    }
                  }}
                />
              )}
            </Box>
          </Box>
        </Box>
      </ChatPanel.Body>

      <AnalysisPanel
        open={analysisOpen}
        onClose={() => setAnalysisOpen(false)}
        prompt={state.prompt}
        conversationJson={JSON.stringify(turnsToConversation(state.turns))}
        functionsJson={JSON.stringify(state.functions)}
        onApplyImprovedPrompt={(improved) => {
          dispatch({ type: 'SET_PROMPT', prompt: improved });
          setAnalysisOpen(false);
        }}
      />

      <CreateEvalPanel
        open={evalPanelOpen}
        onClose={() => setEvalPanelOpen(false)}
        turns={turnsForEval}
        businessUnitId={state.businessUnitId}
        messageId={messageId}
        onRunEvals={() => setRunEvalsOpen(true)}
      />

      <RunEvalsPanel
        open={runEvalsOpen}
        onClose={() => setRunEvalsOpen(false)}
        businessUnitId={state.businessUnitId}
        messageId={messageId}
      />
    </ChatPanel>
  );
};

export default Playground;
